import { FontIcon, mergeStyles, mergeStyleSets, useTheme } from '@fluentui/react';
import { CSSProperties } from 'react';
import { StepStatus } from '../../../data-types/wizard/StepStatus';
import { stepIconFontSize } from './constants';

export interface IStepNavItemProps {
    content: () => JSX.Element;
    status: StepStatus;
    ariaLabel: string;
    onClick?: (event?: React.MouseEvent<HTMLDivElement>) => void | undefined;
}

const menuItemStyles = {
    display: 'flex',
    flexFlow: 'row nowrap',
    width: 'auto',
    height: 'auto',
    boxSizing: 'border-box',
    gap: '12px',
    padding: '2px',
    cursor: 'pointer',
} as CSSProperties;

const iconClass = mergeStyles({
    fontSize: stepIconFontSize,
    margin: '2px 2px',
});

export const StepNavItem = ({ content, status, onClick, ariaLabel }: IStepNavItemProps): JSX.Element => {
    const { palette } = useTheme();

    const iconClassNames = mergeStyleSets({
        invalid: [{ color: palette.redDark }, iconClass],
        valid: [{ color: palette.themePrimary }, iconClass],
        inProgress: [{ color: palette.tealLight }, iconClass],
        new: [{ color: palette.neutralTertiary }, iconClass],
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // code might be unreliable, checking key and code
        if (event.code === 'Enter' || event.code === 'Space' || event.key === 'Enter' || event.key === 'Space') {
            onClick?.();
        }
    };

    function getLabelDivStyles(status: StepStatus) {
        const labelDivStyles = { paddingBottom: 2 } as CSSProperties;
        switch (status) {
            case StepStatus.InProgress:
                labelDivStyles.fontWeight = 600;
                break;
            case StepStatus.Invalid:
            case StepStatus.Valid:
            case StepStatus.New:
            default:
                break;
        }
        return labelDivStyles;
    }

    function getIcon(status: StepStatus) {
        let name = 'StatusCircleRing';
        switch (status) {
            case StepStatus.Valid:
                name = 'SkypeCircleCheck';
                break;
            case StepStatus.InProgress:
                name = 'CircleFill';
                break;
            case StepStatus.Invalid:
                name = 'StatusErrorFull';
                break;
            case StepStatus.New:
            default:
                break;
        }
        return name;
    }

    function getIconClassName(status: StepStatus) {
        let className = iconClassNames.new;
        switch (status) {
            case StepStatus.Valid:
                className = iconClassNames.valid;
                break;
            case StepStatus.InProgress:
                className = iconClassNames.inProgress;
                break;
            case StepStatus.Invalid:
                className = iconClassNames.invalid;
                break;
            case StepStatus.New:
            default:
                break;
        }
        return className;
    }

    return (
        <div tabIndex={0} onClick={onClick} onKeyDown={handleKeyDown} role="menuitem" style={menuItemStyles} aria-label={ariaLabel}>
            <FontIcon iconName={getIcon(status)} className={getIconClassName(status)}></FontIcon>
            <div style={getLabelDivStyles(status)}>{content()}</div>
        </div>
    );
};
